/*
 * Tabla de rutas completa: las 20 del `app-routing.ts` original, mas la ruta 'login'.
 *
 * Dos diferencias deliberadas respecto al original:
 *
 * 1. `loadComponent` en lugar de `component`. Los componentes ya son standalone, asi que
 *    esto da lazy loading por ruta, que el proyecto nunca tuvo: el NgModule era unico y
 *    monolitico. No cambia el comportamiento, solo divide el bundle.
 * 2. Se agrego la ruta 'login'. El AuthGuard hace `router.navigate(['/login'])` y en el
 *    original NO existe esa ruta: caia al comodin '**' y de ahi a ''. Funcionaba por
 *    accidente. Se declara explicita y se deja '' apuntando al mismo componente para
 *    conservar la entrada actual.
 *
 * IMPORTANTE, los roles de `data.roles` son permisos de acceso: se transcribieron uno
 * por uno del original y no se simplifican. Varios tienen mas de un rol y es facil
 * perderlos al portar; de hecho en una version anterior de este archivo 4 rutas quedaron
 * con menos roles de los que les tocan. El orden de las rutas tambien se conserva, por
 * las tres variantes de `binesAbc` que se distinguen por sus parametros.
 */
import { Routes } from '@angular/router';
import { AuthGuard } from './_guards/auth.guard';
import { Pages, Role } from './_models/oauth.models';

export const routes: Routes = [
	// --- publicas
	{
		path: '',
		loadComponent: () => import('./login/login.component').then((m) => m.LoginComponent),
	},
	{
		path: 'login',
		loadComponent: () => import('./login/login.component').then((m) => m.LoginComponent),
	},
	{
		// Sin guard en el original.
		path: Pages.path_pagenotfound,
		loadComponent: () =>
			import('./pages/pagenotfound/pagenotfound.component').then((m) => m.PagenotfoundComponent),
	},

	// --- protegidas, en el orden del original
	{
		path: Pages.path_cifrascontrol,
		loadComponent: () =>
			import('./pages/cifrascontrol/cifrascontrol.component').then((m) => m.CifrascontrolComponent),
		canActivate: [AuthGuard],
		data: { roles: [Role.Ejecutor, Role.Supervisor, Role.Auditoria] },
	},
	{
		path: Pages.path_bitacotamovim,
		loadComponent: () =>
			import('./pages/bitacorramov/bitacorramov.component').then((m) => m.BitacorramovComponent),
		canActivate: [AuthGuard],
		data: { roles: [Role.Ejecutor, Role.Supervisor, Role.Auditoria] },
	},
	{
		path: Pages.path_binmanager,
		loadComponent: () =>
			import('./pages/binmanager/binmanager.component').then((m) => m.BinmanagerComponent),
		canActivate: [AuthGuard],
		data: { roles: [Role.Ejecutor] },
	},
	{
		// "binesAbc/:grupo"
		path: Pages.path_binesAbc,
		loadComponent: () =>
			import('./pages/bines-abc/bines-abc.component').then((m) => m.BinesAbcComponent),
		canActivate: [AuthGuard],
		data: { roles: [Role.Ejecutor] },
	},
	{
		// "binesAbc"
		path: Pages.path_binesAbcAlta,
		loadComponent: () =>
			import('./pages/bines-abc/bines-abc.component').then((m) => m.BinesAbcComponent),
		canActivate: [AuthGuard],
		data: { roles: [Role.Ejecutor] },
	},
	{
		// "binesAbc/:idBin/:grupo" -- este si tiene tres roles, no solo Ejecutor.
		path: Pages.path_binesAbc_edit,
		loadComponent: () =>
			import('./pages/bines-abc/bines-abc.component').then((m) => m.BinesAbcComponent),
		canActivate: [AuthGuard],
		data: { roles: [Role.Ejecutor, Role.Supervisor, Role.Prevencion] },
	},
	{
		path: Pages.path_busquedasejec,
		loadComponent: () =>
			import('./pages/search-ejecutor/search-ejecutor.component').then(
				(m) => m.SearchEjecutorComponent,
			),
		canActivate: [AuthGuard],
		data: { roles: [Role.Ejecutor] },
	},
	{
		path: Pages.path_busquedassupe,
		loadComponent: () =>
			import('./pages/search-supervisor/search-supervisor.component').then(
				(m) => m.SearchSupervisorComponent,
			),
		canActivate: [AuthGuard],
		data: { roles: [Role.Supervisor] },
	},
	{
		// Misma pantalla que path_busquedassupe, tal como en el original.
		path: Pages.path_checksupe,
		loadComponent: () =>
			import('./pages/search-supervisor/search-supervisor.component').then(
				(m) => m.SearchSupervisorComponent,
			),
		canActivate: [AuthGuard],
		data: { roles: [Role.Supervisor] },
	},
	{
		path: Pages.path_busquedasprev,
		loadComponent: () =>
			import('./pages/search-prevencion/search-prevencion.component').then(
				(m) => m.SearchPrevencionComponent,
			),
		canActivate: [AuthGuard],
		data: { roles: [Role.Prevencion] },
	},
	{
		// detalle-bin-sup exporta DetalleBinComponent, sin sufijo Sup.
		path: Pages.path_detalle_bin,
		loadComponent: () =>
			import('./pages/detalle-bin-sup/detalle-bin-sup.component').then(
				(m) => m.DetalleBinComponent,
			),
		canActivate: [AuthGuard],
		data: { roles: [Role.Supervisor, Role.Ejecutor] },
	},
	{
		path: Pages.path_detalle_bin_prev,
		loadComponent: () =>
			import('./pages/detalle-bin-prev/detalle-bin-prev.component').then(
				(m) => m.DetalleBinPrevComponent,
			),
		canActivate: [AuthGuard],
		data: { roles: [Role.Ejecutor, Role.Prevencion] },
	},
	{
		path: Pages.path_detalle_alerta,
		loadComponent: () =>
			import('./pages/alertas/detalle-alerta/detalle-alerta.component').then(
				(m) => m.DetalleAlertaComponent,
			),
		canActivate: [AuthGuard],
		data: { roles: [Role.Ejecutor, Role.Prevencion, Role.Auditoria, Role.Supervisor] },
	},
	{
		path: Pages.path_alertas,
		loadComponent: () => import('./pages/alertas/alertas.component').then((m) => m.AlertasComponent),
		canActivate: [AuthGuard],
		data: { roles: [Role.Ejecutor, Role.Prevencion, Role.Auditoria, Role.Supervisor] },
	},
	{
		path: Pages.path_administracion,
		loadComponent: () =>
			import('./pages/administracion/administracion.component').then(
				(m) => m.AdministracionComponent,
			),
		canActivate: [AuthGuard],
		data: { roles: [Role.Administrador, Role.Supervisor] },
	},
	{
		path: Pages.path_configuracion,
		loadComponent: () =>
			import('./pages/configuracion/configuracion.component').then((m) => m.ConfiguracionComponent),
		canActivate: [AuthGuard],
		data: { roles: [Role.Administrador, Role.Supervisor] },
	},
	{
		path: Pages.path_movimientos_automaticos,
		loadComponent: () =>
			import('./pages/movimientos-automaticos/movimientos-automaticos.component').then(
				(m) => m.MovimientosAutomaticosComponent,
			),
		canActivate: [AuthGuard],
		data: { roles: [Role.Supervisor, Role.Ejecutor, Role.Auditoria] },
	},

	/*
	 * No se porta: LoginForgeComponent (src/app/loginForge). Se importa en el
	 * app-routing.ts original pero no lo usa ninguna ruta. Es codigo muerto, como lo
	 * eran jwt.interceptor.ts y cerrarProcesoCompleto() de detalle-bin-prev.
	 */

	{ path: '**', redirectTo: '' },
];
